import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ColumnDef, Row } from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router-dom';

import { PriceChange } from 'pages/trading/model';
import { calculateDailyStats } from 'pages/trading/price/CalculateDailyStats';

import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { useMeasureFormatter } from 'components/measure-unit/measureFormatter';
import { calcGrowth } from 'utils/calc';
import {
  PriceBreakdownTableRow,
  useCustomCellValueExporters,
  useCustomHeaderExporters,
  usePriceChangeTableColumns,
} from 'pages/trading/price/change/usePriceChangeTableDefinitionHooks';

interface Props {
  data: PriceChange[];
  messageId?: number;
}

export function ProductStyleBreakdownTable({ data, messageId = undefined }: Props) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { measureFormatter } = useMeasureFormatter();

  // temporary hack as we need to know if the analyzer data already contains the new CM1 values to decide whether
  // to show the column group or not
  const containsCM1 = data.some(row => row.fromPrice?.totalCM1 !== undefined);

  const columns = usePriceChangeTableColumns(containsCM1);

  const rows = useMemo(() => {
    return data
      .map(styleRow => {
        const oldStats = calculateDailyStats(styleRow.fromPrice);
        const newStats = calculateDailyStats(styleRow.toPrice);

        const styleParts = styleRow.styNumber?.split('|') || [];
        const variantColor = styleParts[1] || intl.formatMessage({ id: 'trading.price_change.table.style' });

        const rowPriceChangeDelta = calcGrowth(newStats.price, oldStats.price);

        return {
          id: styleRow.styNumber || `${styleRow.prdNumber}-${styleRow.chnName}`,
          artImageUrl: 'imageUrl' in styleRow ? ((styleRow as { imageUrl?: string }).imageUrl ?? '') : '',
          prdName: styleRow.prdName || styleRow.prdNumber,
          styBrandColor: variantColor,
          chnName: styleRow.chnName || '',
          oldPrice: oldStats.price,
          newPrice: newStats.price,
          priceChangeDelta: rowPriceChangeDelta,
          dailyGmv: newStats.dailyGmv,
          dailyGmvDelta: calcGrowth(newStats.dailyGmv, oldStats.dailyGmv),
          oldDailyGmv: oldStats.dailyGmv,
          dailyQty: newStats.dailyQty,
          oldDailyQty: oldStats.dailyQty,
          dailyQtyDelta: calcGrowth(newStats.dailyQty, oldStats.dailyQty),
          originalData: styleRow,
          totalCM1: styleRow.toPrice?.totalCM1,
          cm1Delta:
            styleRow.fromPrice?.totalCM1 && styleRow.toPrice?.totalCM1
              ? calcGrowth(styleRow.toPrice.totalCM1, styleRow.fromPrice.totalCM1)
              : undefined,
        };
      })
      .sort((a, b) => b.dailyGmv - a.dailyGmv);
  }, [data, intl]);

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row.original as PriceBreakdownTableRow;
    if (!castedRow.originalData) return;

    const styleRow = castedRow.originalData;
    const styleUniqueId = styleRow.styNumber || styleRow.prdNumber;
    const targetId = encodeURIComponent(`${styleUniqueId}::${styleRow.chnName || ''}`);

    const currentSearchParams = new URLSearchParams(location.search);
    navigate({
      pathname: `/steeringhub/insightsfeed/price-analysis/${messageId}/style/${targetId}`,
      search: currentSearchParams.toString(),
    });
  };

  const customHeaderExporters = useCustomHeaderExporters();
  const customCellValueExporters = useCustomCellValueExporters();

  const exportOptions: DataTableExportConfigOptions = useMemo(() => {
    return {
      exportName: `price-analysis-breakdown-${messageId}`,
      skippedColumns: ['image'],
      customHeaderExporters,
      customCellValueExporters,
    };
  }, [messageId, measureFormatter]);

  return (
    <DataTable
      titleId={intl.formatMessage({
        id: 'trading.price_change.breakdown.title',
      })}
      columns={columns as ColumnDef<unknown>[]}
      rows={rows}
      onRowClick={messageId ? onRowClick : undefined}
      exportConfigOptions={exportOptions}
    />
  );
}
